import React, { FC, useEffect, useState } from 'react';

import { Button, Form, Input, Row } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import * as api from 'services/api';
import apiPaths from 'services/apiPaths';
import CustomModal from 'src/components/CustomModal';
import { useRouter } from 'next/router';

export interface IRecovery {
  email: string;
}

export interface IRememberPasswordForm {
  loginRef: () => React.MutableRefObject<any>;
  email?: string 
}

const RememberPasswordForm: FC<IRememberPasswordForm> = ({ loginRef, email}) => {
  
  const [emailValue, setEmailValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form] = Form.useForm();
  const { formatMessage } = useIntl();
    
  const { query } = useRouter();
  const isPregnancy = !!query.pregnancy && query.pregnancy === 'true';

  useEffect(() => {
    if(email) {
      form.setFieldsValue({email})
      setEmailValue(email)
    }
  }, [email, form]);
  
  const handleSubmit = async ({ email }: IRecovery) => {
    let callConfig: any = {
      params: {
        email,
      },
    };
    if (isPregnancy) callConfig.params.pregnancy = 'true';

    try {
      await api.recoveryPassword({
        dataPath: apiPaths.RECOVERY_PASSWORD,
        callConfig,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitted(true);
      setLoading(false);
    }
  };

  const handleCancelModal = () => {
    form.resetFields();
    setModalVisible(false);
    setSubmitted(false);
    setLoading(false);
    loginRef().current.scrollIntoView();
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    loginRef().current.scrollIntoView();
  };

  const disableButton = () => {
    if (emailValue !== null && !form.getFieldError('email').length) {
      return false
    };

    return !form.isFieldTouched('email') || !!form.getFieldError('email').length;
  };

  const rememberForm = (
    <div className="recoveryPage con__recoveryPage">
      <Row justify="center" className="recoveryPage__container">
        <span className="recoveryPage__title">
          <FormattedMessage id="recovery.title" />
        </span>

        <p className="recoveryPage__subtitle">
          <FormattedMessage id="recovery.password-message" />
        </p>

        <Row justify="start">
          <Form
            form={form}
            layout="vertical"
            className="recoveryForm"
            initialValues={{email: emailValue}}
            onFinish={handleSubmit}
            validateMessages={{
              required: formatMessage({ id: 'field.required' }),
            }}>
            <Form.Item
              name="email"
              rules={[
                {
                  type: 'email',
                  message: formatMessage({
                    id: 'register.form.email-not-valid',
                  }),
                },
                {
                  required: true,
                  type: 'string',
                  message: formatMessage(
                    { id: 'form.field.required' },
                    { name: formatMessage({ id: 'form.field.email' }) },
                  ),
                },
              ]}>
              <div className="custom-text-field">
                <Input
                  defaultValue={emailValue}
                />
                <label id="email">
                  <FormattedMessage id="form.field.email.label" />
                </label>
              </div>
            </Form.Item>

            <Form.Item shouldUpdate>
              {() => (
                <Button
                  id="recoveryPageBeforeSubmit"
                  className="btn-primary btn-primary--black recoveryPage__button"
                  loading={loading}
                  disabled={
                    disableButton()
                  }
                  htmlType="submit">
                  <FormattedMessage id="recovery.password.button" />
                </Button>
              )}
            </Form.Item>
          </Form>
        </Row>
      </Row>
    </div>
  );
  const rememberSubmitted = (
    <div className="recoveryPage con__recoveryPage">
      <Row justify="center" className="recoveryPage__container">
        <span className="recoveryPage__title">
          <FormattedMessage id="recovery.title.success" />
        </span>
        <p className="recoveryPage__subtitle">
          <FormattedMessage id="recovery.password-message-success" />
        </p>
        <Button
          id="recoveryPageSubmitted"
          className="btn-primary btn-primary--black recoveryPage__button"
          onClick={() => handleCloseModal()}>
          <FormattedMessage id="recovery.password.button.back" />
        </Button>
      </Row>
    </div>
  );

  return (
    <>
      <div className="block--small loginForm-con__login-options--forgot">
        <a className="link" onClick={() => setModalVisible(true)}>
          <FormattedMessage id="form.field.forgot-password" />
        </a>
      </div>
      <CustomModal
        centered
        {...{
          content: submitted ? rememberSubmitted : rememberForm,
          showIcon: false,
          visible: modalVisible,
          footer: null,
          className: 'recover-password-modal',
          onCancel: handleCancelModal,
        }}
      />
    </>
  );
};

export default RememberPasswordForm;
